import partial from 'lodash/partial'
import type {Moment} from 'moment-timezone'
import moment from 'moment-timezone'
import {exhaustive} from '@freckle/exhaustive'
import {mmap, maybe, fromMaybe} from '@freckle/maybe'
import type {AssignmentContextArg} from '@freckle/educator-entities/ts/common/helpers/assignment-context'
import type {RlDomainIdT} from '@freckle/educator-entities/ts/common/types/rl-domain-id'
import type {MultiCourseAssignResponse} from '@freckle/classroom/ts/common/logic/assignments'
import type {SelectedCourseToStudentIdsMapT} from '@freckle/classroom/ts/common/logic/student-course-picker'
import type {AssignmentSubmitParamsT} from '@freckle/classroom/ts/ela/adaptive-skills/components/assign-skill-practice/assign-skill-form-container'
import type {SelectedContentT} from '@freckle/classroom/ts/ela/adaptive-skills/components/assign-skill-practice/assign-skill-form-container/selections'
import type {CreateElaTargetedParams} from '@freckle/classroom/ts/ela/adaptive-skills/helpers/api-helper'
import {
  createElaAdaptiveSkillsAssignment,
  createElaTargetedSkillsPracticeAssignment
} from '@freckle/classroom/ts/ela/adaptive-skills/helpers/api-helper'

function createMultipleCoursePromises(
  requestFn: (studentIds: Array<number>, contextArg: AssignmentContextArg) => Promise<{id: number}>,
  selectedStudents: SelectedCourseToStudentIdsMapT,
  assignmentStartsAt: Moment
): Promise<MultiCourseAssignResponse> {
  const courseIds: number[] = Array.from(selectedStudents.keys())

  const requests = courseIds.map(courseId =>
    maybe(
      () => Promise.resolve(null),
      (studentIds: number[]) =>
        requestFn(studentIds, {
          courseId,
          assignmentStartsAt
        }).then(result => ({
          assignmentId: result.id,
          studentIds,
          courseId
        })) as Promise<{assignmentId: number; studentIds: number[]; courseId: number} | null>,
      selectedStudents.get(courseId)
    )
  )

  return Promise.all(requests).then(results =>
    results.filter((r): r is NonNullable<typeof r> => r !== null)
  )
}

export function createAssignmentPromise(
  selectedStudents: SelectedCourseToStudentIdsMapT,
  assignmentParams: AssignmentSubmitParamsT<
    Exclude<SelectedContentT, {tag: 'word-study-practice'}>
  >,
  selectedStartsAt: Moment | null | undefined,
  elaStandardSetId: string,
  unifiedAssignmentId?: string | null
): Promise<MultiCourseAssignResponse> {
  const startsAt = fromMaybe(() => moment(), selectedStartsAt)
  const startsAtMilliseconds = mmap(startsAt => startsAt.valueOf(), selectedStartsAt)

  if (assignmentParams.assignmentType === 'adaptive') {
    const {selectedDomain} = assignmentParams

    const requestFn = (
      domainId: RlDomainIdT,
      startsAt: number | null | undefined,
      studentIds: Array<number>,
      contextArg: AssignmentContextArg
    ) =>
      createElaAdaptiveSkillsAssignment(
        domainId,
        elaStandardSetId,
        startsAt,
        studentIds,
        contextArg
      ).then(res => ({
        id: res.assignmentId
      }))

    return createMultipleCoursePromises(
      partial(requestFn, selectedDomain.id, startsAtMilliseconds),
      selectedStudents,
      startsAt
    )
  } else {
    const {selectedSkillId, selectedStandardId, selectedContent, skillsForQuestions, title} =
      assignmentParams

    const params = makeTargetedParams(selectedContent)
    const unifiedCourseId = Array.from(selectedStudents.keys())[0] ?? null
    const requestFn = (studentIds: Array<number>, contextArg: AssignmentContextArg) =>
      createElaTargetedSkillsPracticeAssignment(
        selectedSkillId,
        selectedStandardId ?? null,
        elaStandardSetId,
        skillsForQuestions,
        startsAtMilliseconds,
        params,
        studentIds,
        contextArg,
        title,
        unifiedAssignmentId,
        unifiedCourseId
      ).then(({assignmentId: id}) => ({id}))
    return createMultipleCoursePromises(requestFn, selectedStudents, startsAt)
  }
}

const makeTargetedParams = (
  selectedContent: Exclude<SelectedContentT, {tag: 'word-study-practice'}>
): CreateElaTargetedParams => {
  switch (selectedContent.tag) {
    case 'grammar-practice':
      return {questionIds: selectedContent.questions.map(q => q.uuid)}
    case 'skills-practice':
      return {contentIds: selectedContent.contentIds}
    default:
      return exhaustive(selectedContent)
  }
}
